import React, { useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import NotificationsIcon from "@material-ui/icons/Notifications";
import WarningIcon from "@material-ui/icons/Warning";

import Button from "designV2/components/Button";
import DialogShell from "designV2/components/DialogShell";
import Field from "designV2/components/Field";
import PageHeader from "designV2/components/PageHeader";
import Panel from "designV2/components/Panel";
import SearchField from "designV2/components/SearchField";
import { readableError } from "superAdminViews/setting/SettingsList";
import "superAdminViews/setting/settingsList.css";
import "./sendNotif.css";

const SEND_TO_ALL_DRIVERS = gql`
  mutation sendNotificationsToAllDriversByAdmin(
    $title: String!
    $body: String!
    $type: NotificationType!
  ) {
    sendNotificationsToAllDriversByAdmin(title: $title, body: $body, type: $type) {
      message
    }
  }
`;

const SEND_TO_ALL_USERS = gql`
  mutation sendNotificationsToAllUsersByAdmin(
    $title: String!
    $body: String!
    $type: NotificationType!
  ) {
    sendNotificationsToAllUsersByAdmin(title: $title, body: $body, type: $type) {
      message
    }
  }
`;

const SEND_TO_MANY = gql`
  mutation sendNotificationsToMultiDriverOrUserByAdmin(
    $drivers: [ID!]
    $users: [ID!]
    $title: String!
    $body: String!
    $type: NotificationType!
  ) {
    sendNotificationsToMultiDriverOrUserByAdmin(
      input: { users: $users, drivers: $drivers, title: $title, body: $body, type: $type }
    ) {
      message
    }
  }
`;

const GET_DRIVERS = gql`
  query getDriversByAdmin($limit: Int, $skip: Int, $fullName: String) {
    getDriversByAdmin(pagination: { limit: $limit, skip: $skip }, filters: { fullName: $fullName }) {
      _id
      fullName
      phoneNumber
    }
  }
`;

const GET_USERS = gql`
  query getUsersByAdmin($fullName: String, $limit: Int, $skip: Int) {
    getUsersByAdmin(filters: { fullName: $fullName }, pagination: { limit: $limit, skip: $skip }) {
      _id
      fullName
      phoneNumber
    }
  }
`;

const AUDIENCES = [
  { value: "ALL_USERS", label: "Every customer", audience: "user", everyone: true },
  { value: "ALL_DRIVERS", label: "Every driver", audience: "driver", everyone: true },
  { value: "SOME_USERS", label: "Chosen customers", audience: "user", everyone: false },
  { value: "SOME_DRIVERS", label: "Chosen drivers", audience: "driver", everyone: false },
];

const TYPES = [
  { value: "GENERAL", label: "General", hint: "Everyday announcements." },
  { value: "IMPORTANT", label: "Important", hint: "Shown more prominently in the app." },
  { value: "PRIVATE", label: "Private", hint: "For one person's own account." },
];

const TITLE_LIMIT = 65;
const BODY_LIMIT = 240;

/**
 * SendNotif - the push notification composer.
 *
 * The screen sends to everybody or to a hand-picked set, and sending
 * to everybody cannot be taken back — so the audience is chosen first,
 * the message is previewed as it will land on a phone, and a broadcast
 * asks for confirmation before it goes.
 */
const SendNotif = () => {
  const [audience, setAudience] = useState("ALL_USERS");
  const [type, setType] = useState("GENERAL");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [recipients, setRecipients] = useState([]);

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [sent, setSent] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);

  const chosen = AUDIENCES.find((option) => option.value === audience);
  const isDriverAudience = chosen.audience === "driver";
  const toEveryone = chosen.everyone;

  const [findDrivers, { data: driversData, loading: driversLoading }] = useLazyQuery(
    GET_DRIVERS,
    { fetchPolicy: "network-only" }
  );
  const [findUsers, { data: usersData, loading: usersLoading }] = useLazyQuery(GET_USERS, {
    fetchPolicy: "network-only",
  });

  const [sendToAllDrivers] = useMutation(SEND_TO_ALL_DRIVERS);
  const [sendToAllUsers] = useMutation(SEND_TO_ALL_USERS);
  const [sendToMany] = useMutation(SEND_TO_MANY);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    if (toEveryone) return;
    const variables = { limit: 20, skip: 0, fullName: debouncedSearch };
    if (isDriverAudience) findDrivers({ variables });
    else findUsers({ variables });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, isDriverAudience, toEveryone]);

  // Switching between customers and drivers clears the picks: an id
  // from one list means nothing to the other.
  useEffect(() => {
    setRecipients([]);
  }, [audience]);

  const searching = isDriverAudience ? driversLoading : usersLoading;
  const results = isDriverAudience
    ? (driversData && driversData.getDriversByAdmin) || []
    : (usersData && usersData.getUsersByAdmin) || [];

  const toggleRecipient = (person) =>
    setRecipients((current) =>
      current.some((item) => item._id === person._id)
        ? current.filter((item) => item._id !== person._id)
        : current.concat(person)
    );

  const validate = () => {
    const next = {};
    if (!title.trim()) next.title = "Give the notification a title";
    else if (title.length > TITLE_LIMIT) next.title = `Keep it under ${TITLE_LIMIT} characters`;

    if (!body.trim()) next.body = "Write the message";
    else if (body.length > BODY_LIMIT) next.body = `Keep it under ${BODY_LIMIT} characters`;

    if (!toEveryone && recipients.length === 0) {
      next.recipients = "Choose at least one recipient";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onReview = () => {
    setFormError("");
    setSent("");
    if (!validate()) return;
    setConfirming(true);
  };

  const send = () => {
    if (sending) return;
    setSending(true);
    setFormError("");

    const variables = { title: title.trim(), body: body.trim(), type };
    let request;

    if (toEveryone) {
      request = isDriverAudience
        ? sendToAllDrivers({ variables })
        : sendToAllUsers({ variables });
    } else {
      const ids = recipients.map((person) => person._id);
      request = sendToMany({
        variables: Object.assign(
          {},
          variables,
          isDriverAudience ? { drivers: ids } : { users: ids }
        ),
      });
    }

    request
      .then(() => {
        setSending(false);
        setConfirming(false);
        setSent(
          toEveryone
            ? `Sent to every ${isDriverAudience ? "driver" : "customer"}.`
            : `Sent to ${recipients.length} ${
                isDriverAudience ? "driver" : "customer"
              }${recipients.length === 1 ? "" : "s"}.`
        );
        setTitle("");
        setBody("");
        setRecipients([]);
      })
      .catch((err) => {
        setSending(false);
        setConfirming(false);
        setFormError(readableError(err));
      });
  };

  const audienceLabel = toEveryone
    ? chosen.label.toLowerCase()
    : `${recipients.length} chosen ${isDriverAudience ? "driver" : "customer"}${
        recipients.length === 1 ? "" : "s"
      }`;

  return (
    <div className="page settingsPage">
      <main className="main">
        <PageHeader
          title="Send a notification"
          description="Push a message to the customer or driver apps."
          actions={
            <Button variant="primary" onClick={onReview}>
              Review and send
            </Button>
          }
        />

        {formError && (
          <div className="dsFormMessage error" role="alert">
            <ErrorOutlineIcon />
            <span>{formError}</span>
          </div>
        )}
        {sent && (
          <div className="dsFormMessage success" role="status">
            <CheckCircleOutlineIcon />
            <span>{sent}</span>
          </div>
        )}

        <div className="notifLayout">
          <div className="notifMain">
            <Panel title="Who gets it">
              <div className="notifOptionRow">
                {AUDIENCES.map((option) => (
                  <label
                    key={option.value}
                    className={`dsOptionCard ${audience === option.value ? "isSelected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="audience"
                      checked={audience === option.value}
                      onChange={() => setAudience(option.value)}
                    />
                    <span className="dsOptionCardText">
                      <strong>{option.label}</strong>
                    </span>
                  </label>
                ))}
              </div>

              {!toEveryone && (
                <div className="notifPicker">
                  <SearchField
                    placeholder={`Search ${isDriverAudience ? "drivers" : "customers"} by name`}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />

                  {recipients.length > 0 && (
                    <div className="notifChips">
                      {recipients.map((person) => (
                        <button
                          key={person._id}
                          type="button"
                          className="notifChip"
                          onClick={() => toggleRecipient(person)}
                          title="Remove"
                        >
                          {person.fullName}
                          <span aria-hidden="true">&times;</span>
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="notifResults">
                    {searching ? (
                      <p className="notifResultsEmpty">Searching</p>
                    ) : results.length === 0 ? (
                      <p className="notifResultsEmpty">
                        {debouncedSearch
                          ? "Nobody matches that name."
                          : "Start typing a name."}
                      </p>
                    ) : (
                      results.map((person) => {
                        const picked = recipients.some((item) => item._id === person._id);
                        return (
                          <button
                            key={person._id}
                            type="button"
                            className={`notifPerson ${picked ? "isSelected" : ""}`}
                            onClick={() => toggleRecipient(person)}
                          >
                            <span className="notifPersonBody">
                              <strong>{person.fullName}</strong>
                              <span>{person.phoneNumber}</span>
                            </span>
                            <span className="notifPersonCheck">
                              <CheckCircleIcon />
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                  {errors.recipients && (
                    <span className="dsFieldError">{errors.recipients}</span>
                  )}
                </div>
              )}
            </Panel>

            <Panel title="The message">
              <Field
                label="Title"
                htmlFor="notifTitle"
                error={errors.title}
                hint={`${title.length} of ${TITLE_LIMIT} characters`}
              >
                <input
                  id="notifTitle"
                  className="dsInput"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </Field>

              <Field
                label="Message"
                htmlFor="notifBody"
                error={errors.body}
                hint={`${body.length} of ${BODY_LIMIT} characters. Phones cut off what does not fit.`}
              >
                <textarea
                  id="notifBody"
                  className="dsInput settingsTextarea"
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                />
              </Field>

              <Field label="Importance">
                <div className="notifOptionRow">
                  {TYPES.map((option) => (
                    <label
                      key={option.value}
                      className={`dsOptionCard ${type === option.value ? "isSelected" : ""}`}
                    >
                      <input
                        type="radio"
                        name="type"
                        checked={type === option.value}
                        onChange={() => setType(option.value)}
                      />
                      <span className="dsOptionCardText">
                        <strong>{option.label}</strong>
                        <span>{option.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </Field>
            </Panel>
          </div>

          {/* Nobody could see what they were about to send to thousands
              of phones until it had already arrived on them. */}
          <aside className="notifPreviewPane">
            <Panel title="Preview" subtitle="Roughly how this lands on a phone.">
              <div className="notifPhone">
                <div className="notifCard">
                  <span className="notifCardIcon">
                    <NotificationsIcon />
                  </span>
                  <div className="notifCardBody">
                    <div className="notifCardHead">
                      <strong>Spark</strong>
                      <span>now</span>
                    </div>
                    <p className="notifCardTitle">{title || "Notification title"}</p>
                    <p className="notifCardText">
                      {body || "The message you write appears here."}
                    </p>
                  </div>
                </div>
              </div>
              <p className="notifPreviewNote">Going to {audienceLabel}.</p>
            </Panel>
          </aside>
        </div>
      </main>

      <DialogShell
        open={confirming}
        onClose={() => setConfirming(false)}
        title={toEveryone ? "Send to everyone?" : "Send this notification?"}
        type={toEveryone ? "error" : "info"}
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setConfirming(false), variant: "secondary" },
          {
            label: "Send",
            onClick: send,
            variant: toEveryone ? "danger" : "primary",
            loading: sending,
          },
        ]}
      >
        <div className="settingsForm">
          {toEveryone && (
            <div className="dsFormMessage warning">
              <WarningIcon fontSize="small" />
              <span>
                This reaches every {isDriverAudience ? "driver" : "customer"} on the platform and
                cannot be recalled.
              </span>
            </div>
          )}
          <div className="notifConfirm">
            <strong>{title}</strong>
            <p>{body}</p>
            <span>Going to {audienceLabel}.</span>
          </div>
        </div>
      </DialogShell>
    </div>
  );
};

export default SendNotif;
